/**
 * Activity-log transformer
 *
 * Converts the flat row response from POST /consent/activityLogs into a
 * grouped-by-attempt structure for the UserActivityLogsView timeline UI.
 */

import type {
  ActivityLogAttempt,
  ActivityLogEntry,
  ActivityLogTimeline,
} from '../../../types/timeline';

/** Raw row from API or dummy data — matches service response shape. */
interface RawActivityRow {
  timestamp: string;
  time_in_millis?: number | null;
  event_code: string;
  label?: string;
  message?: string;
  status?: string;
  attempt_id: string;
  transaction_id?: string;
}

/** Event codes that mark an attempt as having reached a terminal state. */
const APPROVED_CODES = new Set(['OM-CONSENT-APPROVED', 'CONSENT-APPROVED']);
const REJECTED_CODES = new Set(['OM-CONSENT-REJECTED', 'CONSENT-REJECTED']);
const CANCELLED_CODES = new Set(['OM-CANCEL', 'CANCEL']);

function deriveOutcome(events: ActivityLogEntry[]): ActivityLogAttempt['outcome'] {
  if (events.some(e => APPROVED_CODES.has(e.event_code))) return 'APPROVED';
  if (events.some(e => REJECTED_CODES.has(e.event_code))) return 'REJECTED';
  if (events.some(e => CANCELLED_CODES.has(e.event_code))) return 'CANCELLED';
  // If user reached a meaningful mid-stage but no terminal event → DROPPED
  const progressedMarkers = [
    'OM-LINK-CONFIRM',
    'OM-DISCOVERED',
    'OM-SELECT-FIP',
    'OM-FIP-SUBMIT-CLICKED',
    'OM-VERIFY-OTP',
    'OM-USER-LOGIN-SUCCESS',
    'OM-INIT-OTP',
    'OM-LOADED',
  ];
  if (events.some(e => progressedMarkers.includes(e.event_code))) return 'DROPPED';
  return 'IN_PROGRESS';
}

function extractExitReason(events: ActivityLogEntry[]): string | undefined {
  const exit = events.find(e => e.event_code === 'OM-CANCEL' || e.event_code === 'OM-EXIT-FEEDBACK');
  if (exit && exit.message && exit.message.trim() && exit.message.trim().toLowerCase() !== 'user cancelled onemoney flow') {
    return exit.message.trim();
  }
  return undefined;
}

/**
 * Group a flat list of activity events by attempt_id and produce an
 * ActivityLogTimeline with attempts ordered newest-first.
 */
export function transformActivityLogs(rows: RawActivityRow[]): ActivityLogTimeline {
  if (!rows || rows.length === 0) {
    return { attempts: [], total_events: 0 };
  }

  // Bucket by attempt_id
  const buckets = new Map<string, ActivityLogEntry[]>();
  for (const r of rows) {
    if (!r.attempt_id) continue;
    const entry: ActivityLogEntry = {
      timestamp: r.timestamp,
      time_in_millis: r.time_in_millis ?? null,
      event_code: r.event_code,
      label: r.label || r.event_code,
      message: r.message,
      status: r.status,
      attempt_id: r.attempt_id,
      transaction_id: r.transaction_id,
    };
    const arr = buckets.get(r.attempt_id) ?? [];
    arr.push(entry);
    buckets.set(r.attempt_id, arr);
  }

  // Build attempts, sort events within each newest-first, compute outcome
  const attemptsRaw = Array.from(buckets.entries()).map(([attempt_id, events]) => {
    const sortedDesc = [...events].sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1));
    const sortedAsc = [...sortedDesc].reverse();
    return {
      attempt_id,
      start_time: sortedAsc[0].timestamp,
      end_time: sortedAsc[sortedAsc.length - 1].timestamp,
      events: sortedDesc,
      outcome: deriveOutcome(sortedAsc),
      exit_reason: extractExitReason(sortedAsc),
    };
  });

  // Sort attempts by start_time ascending to get chronological attempt_number,
  // then reverse for newest-first display.
  attemptsRaw.sort((a, b) => (a.start_time < b.start_time ? -1 : 1));
  const numbered: ActivityLogAttempt[] = attemptsRaw.map((a, idx) => ({
    ...a,
    attempt_number: idx + 1,
  }));

  numbered.sort((a, b) => (a.start_time < b.start_time ? 1 : -1)); // newest first

  return {
    attempts: numbered,
    total_events: numbered.reduce((sum, attempt) => sum + attempt.events.length, 0),
  };
}
